'use client';

import React, { useState } from 'react'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ArrowRight, Flame, Code, Terminal, FormInput, Link2, 
  CheckCircle2, ChevronDown, Sparkles, Zap, Laptop, Play
} from 'lucide-react'
import { MarketingNavbar } from '@/components/layout/marketing-navbar'
import { MarketingFooter } from '@/components/layout/marketing-footer'
import { GeometricBackground } from '@/components/ui/geometric-background'
import { ShinyButton } from '@/components/ui/shiny-button'
import { DashboardPreview } from '@/components/ui/dashboard-animation'

export default function FoundersClient() {
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  const faqs = [
    {
      q: "Is there a free tier for bootsrapping founders?",
      a: "Yes. Our free tier allows you to launch waitlists, create 50 smart links, generate 20 dynamic QR codes, and sell up to 2 digital products with direct Stripe payouts. Perfect for validating your first MVP."
    },
    {
      q: "How does the REST API integrate with my SaaS product?",
      a: "You can generate developer API tokens in your account dashboard. Use our simple endpoints to create short links on the fly, track referrals, or query click statistics programmatically."
    },
    {
      q: "How do I track waitlist signups?",
      a: "Lenklyst captures detailed analytics for every signup, including daily growth charts, referrers, and geolocation logs to trace where your launch traction is coming from."
    },
    {
      q: "How long does it take to deploy a waitlist form?",
      a: "Less than 2 minutes. You can create a lead capture form, adjust the copy, set up your milestone rewards, and embed it or link it directly on your social channels."
    }
  ]

  return (
    <div className="relative min-h-screen bg-black overflow-x-clip selection:bg-primary/30 text-white font-sans">
      <GeometricBackground />
      <MarketingNavbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 max-w-[1400px] mx-auto z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center min-h-[70vh]">
          {/* Left Column: Hero Copy */}
          <div className="lg:col-span-7 space-y-6 flex flex-col items-start text-left">
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-primary/20 text-primary text-[10px] font-bold tracking-widest uppercase rounded-full">
              <Laptop className="w-3.5 h-3.5 text-primary" /> For Indie Hackers &amp; Solo Founders
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter uppercase leading-[1.05]">
              Validate Fast.<br />
              <span className="text-gray-500">Build your list.</span><br />
              Scale your launch.
            </h1>
            <p className="text-base sm:text-lg text-white/50 font-light max-w-xl leading-relaxed">
              The lightweight, high-performance marketing stack for founders. Setup waitlist milestones, gather product feedback, generate CNAME short links, and hook up developer webhooks.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
              <Link href="/auth/sign-up" className="w-full sm:w-auto">
                <ShinyButton className="w-full justify-center py-4 px-8 text-base">
                  Start Validating Free <ArrowRight className="w-4 h-4 ml-1" />
                </ShinyButton>
              </Link>
              <a href="#benefits" className="w-full sm:w-auto text-center py-3.5 px-8 rounded-full border border-white/10 text-white/60 hover:text-white hover:border-white/20 transition-all font-semibold text-sm">
                View Developer Docs
              </a>
            </div>
          </div>

          {/* Right Column: Dashboard Preview */}
          <div className="lg:col-span-5 flex justify-center lg:justify-end relative">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-primary/10 rounded-full blur-[100px] pointer-events-none" />
            <div className="w-full max-w-[460px] relative z-10">
              <DashboardPreview sectionId="links" className="shadow-[0_0_50px_rgba(59,130,246,0.15)]" />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-24 border-t border-white/5 relative z-10">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="space-y-4 mb-16 text-center">
            <span className="text-[10px] font-bold tracking-[0.2em] text-primary uppercase">Startup Foundation</span>
            <h2 className="text-3xl sm:text-5xl font-bold uppercase tracking-tighter">
              Validate ideas before writing code.
            </h2>
            <p className="text-sm sm:text-base text-white/40 max-w-lg mx-auto font-light">
              Get landing waitlists, tracking parameters, and feedback forms running in minutes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Waitlists */}
            <div className="p-8 bg-white/[0.01] hover:bg-white/[0.02] border border-white/5 rounded-[2rem] transition-all flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="p-3 bg-white/5 rounded-2xl w-fit">
                  <FormInput className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold uppercase tracking-tight">Pre-Launch Waitlists</h3>
                <p className="text-sm text-white/50 font-light leading-relaxed">
                  Collect email addresses and gauge market demand before launching on Product Hunt. Fast layouts, no external hosting, and responsive widgets built-in.
                </p>
              </div>
              <ul className="space-y-2 border-t border-white/[0.04] pt-6">
                {['Embeddable lead forms', 'Custom branding styling controls', 'Spam & bot detection blocks'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-xs text-white/75 font-light">
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Referrals */}
            <div className="p-8 bg-white/[0.01] hover:bg-white/[0.02] border border-white/5 rounded-[2rem] transition-all flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="p-3 bg-white/5 rounded-2xl w-fit">
                  <Zap className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold uppercase tracking-tight">Milestone Referral Engine</h3>
                <p className="text-sm text-white/50 font-light leading-relaxed">
                  Let your early adopters do the heavy marketing. Reward users with discounts, downloads, or custom access codes when they refer friends to sign up.
                </p>
              </div>
              <ul className="space-y-2 border-t border-white/[0.04] pt-6">
                {['Automatic referral invite keys', 'Queue skip positions dashboards', 'Milestone achievement triggers'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-xs text-white/75 font-light">
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Smart links */}
            <div className="p-8 bg-white/[0.01] hover:bg-white/[0.02] border border-white/5 rounded-[2rem] transition-all flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="p-3 bg-white/5 rounded-2xl w-fit">
                  <Link2 className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold uppercase tracking-tight">Branded Campaign Links</h3>
                <p className="text-sm text-white/50 font-light leading-relaxed">
                  Shorten URLs for newsletters or social bios. Add UTM tags automatically to trace which networks (LinkedIn, Twitter, IndieHackers) drive conversions.
                </p>
              </div>
              <ul className="space-y-2 border-t border-white/[0.04] pt-6">
                {['UTM tracking templates parameters', 'Custom alias endpoints slugs', 'Sub-100ms redirections edge paths'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-xs text-white/75 font-light">
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Developer API */}
            <div className="p-8 bg-white/[0.01] hover:bg-white/[0.02] border border-white/5 rounded-[2rem] transition-all flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="p-3 bg-white/5 rounded-2xl w-fit">
                  <Code className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold uppercase tracking-tight">Programmatic REST API</h3>
                <p className="text-sm text-white/50 font-light leading-relaxed">
                  Integrate short link creation and waitlist status verification directly into your custom application code. Build robust automation in minutes.
                </p>
              </div>
              <ul className="space-y-2 border-t border-white/[0.04] pt-6">
                {['API keys dashboard control', 'Webhook streams payload streams', 'Structured JSON documentation guides'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-xs text-white/75 font-light">
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 border-t border-white/5 relative z-10 bg-black/40">
        <div className="max-w-[800px] mx-auto px-6">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-5xl font-bold uppercase tracking-tighter">
              Founder FAQs
            </h2>
            <p className="text-xs sm:text-sm text-white/40 max-w-sm mx-auto font-light">
              How solo builders and indie hackers validate fast.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => {
              const isOpen = openFaq === idx
              return (
                <div 
                  key={idx}
                  className="bg-white/[0.01] hover:bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden transition-all duration-300"
                >
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                    className="w-full flex items-center justify-between p-5 text-left focus:outline-none"
                  >
                    <span className="text-sm font-bold uppercase tracking-tight text-white/90">
                      {faq.q}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-white/40 transition-transform duration-300 ${isOpen ? "rotate-180 text-primary" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="px-5 pb-5 pt-1 text-xs sm:text-sm text-white/50 leading-relaxed font-light border-t border-white/[0.02]">
                          {faq.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-32 border-t border-white/5 relative z-10 overflow-hidden text-center">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-primary/10 rounded-full blur-[160px] pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 relative space-y-8">
          <h2 className="text-4xl sm:text-6xl font-bold tracking-tighter uppercase">
            Validate ideas fast. Build a waiting list.
          </h2>
          <p className="text-sm sm:text-base text-white/40 max-w-md mx-auto font-light leading-relaxed">
            Boost early validation with viral referrals and custom-branded link parameters.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link href="/auth/sign-up">
              <ShinyButton className="px-10 py-4 text-sm font-bold">
                Deploy Founder Setup Free <ArrowRight className="w-4 h-4 ml-1" />
              </ShinyButton>
            </Link>
            <Link href="/pricing" className="text-white/60 hover:text-white border border-white/10 px-8 py-3.5 rounded-full text-sm font-semibold hover:border-white/20 transition-all">
              See Pricing
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}
